// 백엔드 API를 통한 계정 통합 유틸리티 함수들
import { AI_API_URL } from '../../constants';

/**
 * 이메일로 기존 계정 찾기 (포괄적 검색 - Firebase Auth + Firestore)
 * @param {string} email - 검색할 이메일
 * @param {boolean} autoRepair - 자동 복구 활성화 여부 (기본값: true)
 * @returns {Promise<Object|null>} - 포괄적 계정 정보 또는 null
 */
export const findExistingAccountByEmail = async (email, autoRepair = true) => {
  try {
    console.log('🔍 [findExistingAccountByEmail] 포괄적 계정 검색 시작:', { email, autoRepair });
    
    if (!email) {
      console.log('❌ [findExistingAccountByEmail] 이메일이 없음 → null 반환');
      return null;
    }
    
    const response = await fetch(`${AI_API_URL}/api/check-existing-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        email: email,
        auto_repair: autoRepair
      }),
    });

    if (!response.ok) {
      throw new Error(`API 호출 실패: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    console.log('🔍 [findExistingAccountByEmail] API 응답:', data);
    
    if (data.exists) {
      const accountStatus = data.account_status;
      
      if (accountStatus === 'complete_account') {
        console.log('✅ [findExistingAccountByEmail] 완전한 계정 발견:', {
          uid: data.accountInfo.uid,
          email: data.accountInfo.email,
          loginMethod: data.accountInfo.loginMethod,
          emailVerified: data.accountInfo.emailVerified,
          wasRepaired: data.was_repaired
        });
        
        if (data.was_repaired) {
          console.log('🔧 [findExistingAccountByEmail] 계정이 자동 복구되었습니다');
        }
        
        return {
          ...data.accountInfo,
          accountStatus: accountStatus,
          firebaseInfo: data.firebase_info,
          firestoreInfo: data.firestore_info,
          wasRepaired: data.was_repaired
        };
      } else if (accountStatus === 'incomplete_firebase' || accountStatus === 'incomplete_firestore') {
        console.log('⚠️ [findExistingAccountByEmail] 불완전한 계정 발견:', {
          status: accountStatus,
          recommendedAction: data.recommended_action
        });
        
        return {
          ...data.accountInfo,
          accountStatus: accountStatus,
          incomplete: true,
          repairNeeded: true,
          recommendedAction: data.recommended_action,
          firebaseInfo: data.firebase_info,
          firestoreInfo: data.firestore_info
        };
      } else if (accountStatus === 'uid_mismatch') {
        console.log('🚨 [findExistingAccountByEmail] UID 불일치 계정 발견:', {
          firebaseUid: data.accountInfo.firebase_uid,
          firestoreUid: data.accountInfo.firestore_uid
        });
        
        return {
          ...data.accountInfo,
          accountStatus: accountStatus,
          conflict: true,
          recommendedAction: data.recommended_action
        };
      }
    } else {
      console.log('❌ [findExistingAccountByEmail] 계정 없음 → null 반환');
      return null;
    }
  } catch (error) {
    console.error('❌ [findExistingAccountByEmail] 포괄적 계정 검색 실패:', error);
    // 에러가 발생해도 null을 반환하여 로그인 프로세스가 계속 진행되도록 함
    return null;
  }
};

/**
 * 불완전한 계정을 수동으로 복구
 * @param {string} email - 복구할 이메일
 * @returns {Promise<Object>} - 복구 결과
 */
export const repairIncompleteAccount = async (email) => {
  try {
    console.log('🔧 [repairIncompleteAccount] 수동 계정 복구 시작:', { email });
    
    const response = await fetch(`${AI_API_URL}/api/repair-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`API 호출 실패: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    console.log('🔧 [repairIncompleteAccount] 복구 결과:', data);
    
    return data;
  } catch (error) {
    console.error('❌ [repairIncompleteAccount] 계정 복구 실패:', error);
    return {
      success: false,
      message: `계정 복구 중 오류 발생: ${error.message}`
    };
  }
};

/**
 * 계정 상태에 따른 사용자 친화적 메시지 생성
 * @param {Object} accountInfo - 계정 정보
 * @returns {string} - 사용자 메시지
 */
export const getAccountStatusMessage = (accountInfo) => {
  if (!accountInfo) return '';
  
  const { accountStatus, loginMethod, wasRepaired } = accountInfo;
  
  if (wasRepaired) {
    return '계정이 자동으로 복구되었습니다. 정상적으로 이용하실 수 있습니다.';
  }
  
  switch (accountStatus) {
    case 'complete_account':
      const methods = Array.isArray(loginMethod) ? loginMethod : [loginMethod];
      const methodNames = methods.map(method => {
        switch (method) {
          case 'email': return '이메일';
          case 'google': return '구글';
          case 'kakao': return '카카오';
          case 'naver': return '네이버';
          default: return method;
        }
      }).join(', ');
      return `기존 계정이 있습니다. 로그인 방식: ${methodNames}`;
      
    case 'incomplete_firebase':
      return '계정 설정이 불완전합니다. 자동 복구를 시도하거나 고객센터에 문의해주세요.';
      
    case 'incomplete_firestore':
      return '계정 데이터가 불완전합니다. 자동 복구를 시도하거나 고객센터에 문의해주세요.';
      
    case 'uid_mismatch':
      return '계정 정보에 충돌이 있습니다. 고객센터에 문의해주세요.';
      
    default:
      return '계정 상태를 확인할 수 없습니다.';
  }
};

/**
 * OAuth 계정에 이메일 로그인 정보 추가 (백엔드 API 사용)
 * @param {Object} existingAccount - 기존 OAuth 계정 정보
 * @param {string} password - 설정할 비밀번호
 * @param {string} email - 이메일
 * @returns {Promise<Object|boolean>} - 성공 시 응답 데이터, 실패 시 false
 */
export const addEmailAuthToOAuthAccount = async (existingAccount, password, email) => {
  try {
    console.log('🔍 [addEmailAuthToOAuthAccount] 백엔드 API 호출 시작:', {
      uid: existingAccount?.uid,
      email: email,
      currentLoginMethod: existingAccount?.loginMethod,
      hasPassword: !!password,
      accountStatus: existingAccount?.accountStatus
    });
    
    if (!existingAccount || !existingAccount.uid || !password) {
      console.error('❌ [addEmailAuthToOAuthAccount] 유효하지 않은 계정 정보 또는 비밀번호 없음');
      return false;
    }
    
    // 불완전한 계정인 경우 먼저 복구 시도
    if (existingAccount.accountStatus && existingAccount.accountStatus !== 'complete_account') {
      console.log('🔧 [addEmailAuthToOAuthAccount] 불완전한 계정 복구 시도');
      const repairResult = await repairIncompleteAccount(email);
      if (!repairResult.success) {
        console.warn('⚠️ [addEmailAuthToOAuthAccount] 계정 복구 실패, 계속 진행');
      }
    }
    
    const response = await fetch(`${AI_API_URL}/api/link-email-to-oauth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        email: email,
        targetUid: existingAccount.uid,
        password: password
      }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`API 호출 실패: ${response.status} ${response.statusText} - ${errorData}`);
    }

    const data = await response.json();
    
    console.log('🔍 [addEmailAuthToOAuthAccount] API 응답:', data);
    
    if (data.success) {
      console.log(`✅ [addEmailAuthToOAuthAccount] OAuth 계정 ${existingAccount.uid}에 이메일 로그인 추가 완료`);
      return data; // Firebase Token을 포함한 전체 응답 반환
    } else {
      console.log(`⚠️ [addEmailAuthToOAuthAccount] ${data.message}`);
      return false;
    }
  } catch (error) {
    console.error('❌ [addEmailAuthToOAuthAccount] OAuth 계정에 이메일 로그인 추가 실패:', error);
    return false;
  }
};

/**
 * 계정이 OAuth 연동되어 있는지 확인
 * @param {Object} userData - 사용자 데이터
 * @returns {boolean} - OAuth 연동 여부
 */
export const isOAuthLinked = (userData) => {
  if (!userData) return false;
  
  const loginMethods = Array.isArray(userData.loginMethod) 
    ? userData.loginMethod 
    : [userData.loginMethod];
  
  return loginMethods.some(method => ['kakao', 'google', 'naver'].includes(method));
};

/**
 * 이메일 인증이 필요한지 확인 (OAuth 연동 시 불필요)
 * @param {Object} user - Firebase 사용자 객체
 * @param {Object} userData - Firestore 사용자 데이터
 * @returns {boolean} - 이메일 인증 필요 여부
 */
export const isEmailVerificationRequired = (user, userData) => {
  console.log('🔍 [isEmailVerificationRequired] 시작:', {
    user_exists: !!user,
    user_email: user?.email,
    user_emailVerified: user?.emailVerified,
    userData_exists: !!userData,
    userData: userData
  });
  
  if (!user) {
    console.log('❌ [isEmailVerificationRequired] user가 없음 → false 반환');
    return false;
  }
  
  // userData가 없는 경우 (Firestore 권한 에러 등)
  if (!userData) {
    console.log('⚠️ [isEmailVerificationRequired] userData가 없음 - 이메일 인증 상태로 판단');
    // 이메일이 인증되지 않은 경우 인증이 필요하다고 가정
    const needsVerification = !user.emailVerified;
    console.log('🔍 [isEmailVerificationRequired] userData 없음 시 판단:', {
      user_emailVerified: user.emailVerified,
      needsVerification
    });
    return needsVerification;
  }
  
  // OAuth가 연동된 계정은 이메일 인증 불필요
  const isOAuthAccount = isOAuthLinked(userData);
  console.log('🔍 [isEmailVerificationRequired] OAuth 연동 확인:', {
    isOAuthAccount,
    userData_loginMethod: userData.loginMethod
  });
  
  if (isOAuthAccount) {
    console.log('✅ [isEmailVerificationRequired] OAuth 연동 계정 → false 반환');
    return false;
  }
  
  // 순수 이메일 계정만 이메일 인증 필요
  const loginMethods = Array.isArray(userData.loginMethod) 
    ? userData.loginMethod 
    : [userData.loginMethod];
  
  const hasEmailMethod = loginMethods.includes('email');
  const needsVerification = !user.emailVerified && hasEmailMethod;
  
  console.log('🔍 [isEmailVerificationRequired] 최종 판단:', {
    loginMethods,
    hasEmailMethod,
    user_emailVerified: user.emailVerified,
    needsVerification
  });
    
  return needsVerification;
}; 